import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useIsKeyboardOpen } from "@src/appV2/lib/utils/capacitor/useIsKeyboardOpen";
import { useEffect, useRef, useState } from "react";

import { ListLoadingState } from "../../../../../components/ListLoadingState";
import { PageWrapper } from "../../../../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../../../../components/StickyScrollPageHeader";
import { DocumentDetailsMetadata } from "../../../Metadata";
import { DocumentDetailsRequiredBySection } from "../../../RequiredBySection/Section";
import { DocumentPageScrollWrapper } from "../../../ScrollWrapper";
import { useRequirementDetailsContext } from "../../../useRequirementDetailsContext";
import { useGetProfessionalReferences } from "../api/useGetProfessionalReferences";
import { DEFAULT_PAGE_TITLE, REFERENCE_STATUS_SORT_ORDER } from "../constants";
import {
  calculateInitialFormIds,
  generateReferenceFormId,
  getReferencesCountsByStatus,
} from "../utils/formHelpers";
import { ReferenceCard } from "./ReferenceCard";
import { ReferenceFooter } from "./ReferenceFooter";
import { ReferenceForm } from "./ReferenceForm";

export function ProfessionalReferencesWorkflowView() {
  const { requirement: workerRequirement } = useRequirementDetailsContext();
  const scrollRef = useRef<HTMLDivElement>(null);
  const title = workerRequirement?.requirement?.attributes?.name ?? DEFAULT_PAGE_TITLE;

  const { data, isLoading } = useGetProfessionalReferences();
  const isKeyboardOpen = useIsKeyboardOpen({ keyboardOpenEvent: "keyboardWillShow" });

  // Track all form ids (both required and user-added forms)
  const [forms, setForms] = useState<
    Array<{ id: string; initialFocusFirstField?: boolean }> | undefined
  >(undefined);

  useLogEffect(APP_V2_USER_EVENTS.PROFESSIONAL_REFERENCES_PAGE_VIEWED);

  // Initialize form ids once when data loads
  useEffect(() => {
    // Only calculate initial form ids if we don't have any form ids yet and the professional references data is defined
    if (isDefined(forms) || !isDefined(data)) {
      return;
    }

    const initialFormIds = calculateInitialFormIds({
      references: data.references,
      minVerifiedReferenceRequired: data.minVerifiedReferenceRequired,
      completed: data.completed,
    });

    setForms(initialFormIds);
  }, [data, forms]);

  // Add a new reference form
  function handleAddReferenceForm() {
    setForms((previous) => {
      if (!isDefined(previous)) {
        return [];
      }
      return [...previous, { id: generateReferenceFormId(), initialFocusFirstField: true }];
    });
  }

  // Show loading state
  if (isLoading) {
    return (
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          shouldWrapTitle
          title={title}
          headerVariant="tertiary"
          scrollContainerRef={scrollRef}
        />
        <ListLoadingState texts={["Loading references..."]} />
      </PageWrapper>
    );
  }

  const referencesCountsByStatus = getReferencesCountsByStatus(data?.references);
  const referencesCount = data?.references.length ?? 0;
  // Only show footer if there are references, the requirement is not completed, and keyboard is not open
  const showFooter =
    isDefined(data) && data.references.length > 0 && !data.completed && !isKeyboardOpen;

  const sortedReferences = isDefined(data?.references)
    ? [...data.references].sort((a, b) => {
        return (
          (REFERENCE_STATUS_SORT_ORDER[a.status] ?? 0) -
          (REFERENCE_STATUS_SORT_ORDER[b.status] ?? 0)
        );
      })
    : undefined;

  return (
    <PageWrapper variant="tertiary">
      <StickyScrollPageHeader
        shouldWrapTitle
        title={title}
        headerVariant="tertiary"
        scrollContainerRef={scrollRef}
      />

      <DocumentPageScrollWrapper ref={scrollRef}>
        {isDefined(data) && (
          <Text variant="body1">
            Add your professional references below. You&apos;ll need{" "}
            {data.minVerifiedReferenceRequired} approved to complete this step.
          </Text>
        )}

        <DocumentDetailsMetadata />

        {/* Existing References */}
        {isDefined(data) && isDefined(sortedReferences) && sortedReferences.length > 0 && (
          <Stack gap={6}>
            {sortedReferences.map((reference) => (
              <ReferenceCard key={reference._id} reference={reference} completed={data.completed} />
            ))}
          </Stack>
        )}

        {/* Reference Forms */}
        {isDefined(forms) && forms.length > 0 && (
          <Stack gap={6}>
            {forms.map(({ id, initialFocusFirstField }, index) => (
              <ReferenceForm
                key={id}
                id={id}
                count={referencesCount + index + 1}
                initialFocusFirstField={initialFocusFirstField}
                onSuccess={() => {
                  // Remove the form id itself from the array after successful submission
                  setForms((previous) => {
                    if (!isDefined(previous)) {
                      return [];
                    }
                    return previous.filter((form) => form.id !== id);
                  });
                }}
              />
            ))}
          </Stack>
        )}
        <DocumentDetailsRequiredBySection />
      </DocumentPageScrollWrapper>

      {/* Sticky Footer with Add Reference button */}
      {showFooter && (
        <ReferenceFooter
          minRequired={data.minVerifiedReferenceRequired}
          referencesCountsByStatus={referencesCountsByStatus}
          onAddReference={() => {
            handleAddReferenceForm();
          }}
        />
      )}
    </PageWrapper>
  );
}
